import { Button } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { ReactElement, useContext, useEffect, useRef } from 'react';
import { SessionContext } from '../../context/SessionContext';
import { useCurrentSession } from '../../hooks/chat/useCurrentSession';
import { useDefaultQuestions } from '../../hooks/chat/useDefaultQuestions';
import { useSendQuestion } from '../../hooks/chat/useSendQuestion';
import FeedbackForm, { isFeedbackProvided } from '../FeedbackForm';
import LoadingDots from '../LoadingDots/LoadingDots';
import Message from '../Message';
import styles from './ChatMessages.module.scss';

const cx = classNames.bind(styles);

const ChatMessages = (): ReactElement => {
  const { sessionInfo, searchTerm, isLoading } = useContext(SessionContext);
  const { currentQuery, isLoadingCurrentQuery } = sessionInfo;
  const { data: defaultQuestions, isLoading: defaultQuestionsIsLoading } = useDefaultQuestions();
  const { send } = useSendQuestion();

  const { history, sessionId, isLoading: sessionIsLoading } = useCurrentSession();

  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = bodyRef.current?.querySelector('[data-scroll-point="1"]');
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }, [history, isLoadingCurrentQuery, searchTerm]);

  const hasMessages = !!history.length;

  return (
    <div className={cx('body')} ref={bodyRef}>
      {hasMessages
        ? history.map((item) => (
            <React.Fragment key={`message-group-${item.query_seq_id}`}>
              <Message sender="user" text={item.query} date={item.query_dtm} />
              <Message
                sender="genie"
                text={item.cb_response}
                date={item.query_dtm}
                documents={item.source_documents}
                sessionId={sessionId}
                historyItem={item}
              />
              {!isFeedbackProvided(item) && item.usr_thumbs_up !== undefined ? (
                <Message
                  sender="genie"
                  text={<FeedbackForm sessionId={sessionId} historyItem={item} />}
                  date={item.query_dtm}
                  hideActions
                />
              ) : null}
            </React.Fragment>
          ))
        : null}
      {!hasMessages && !isLoadingCurrentQuery && !isLoading && !sessionIsLoading && defaultQuestions?.length ? (
        <Message
          sender="genie"
          text={
            <div className={cx('suggestions')}>
              <p>Here are some questions you can ask me</p>
              {defaultQuestions.map((query) => (
                <Button
                  key={query}
                  className={cx('suggestionsItem')}
                  onClick={() => send({ query, isSuggested: true })}
                >
                  {query}
                </Button>
              ))}
            </div>
          }
          date={new Date().toISOString()}
          hideActions
        />
      ) : null}
      {(isLoadingCurrentQuery || sessionIsLoading || defaultQuestionsIsLoading || isLoading) && (
        <>
          {currentQuery ? <Message sender="user" text={currentQuery} date={new Date().toISOString()} /> : null}
          <Message sender="genie" text={<LoadingDots />} date={new Date().toISOString()} hideActions />
        </>
      )}
      <span data-scroll-point="1" />
    </div>
  );
};

export default ChatMessages;
